import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useWalletsReadContext } from '@context/UserWalletContext'

import { WalletRestrictions, isWalletRestrictionsAllowed } from '@utils/wallet.utils'

import type { TradingAccount as TradingAccountModel } from '@model/TradingAccount'
import type { WalletDto } from '@model/WalletDto'

import type { DepositStartLocationState, DepositTarget } from './types'

export const useDepositSelections = (params: {
  wallets: WalletDto[]
  tradingAccounts: TradingAccountModel[]
  target: DepositTarget
  idParam?: string
}) => {
  const { wallets: contextWallets } = useWalletsReadContext()
  const { wallets, tradingAccounts, target, idParam } = params
  const navigate = useNavigate()
  const location = useLocation()

  const locationState = useMemo(() => {
    const state = (location.state || {}) as DepositStartLocationState
    return {
      ...state,
      isDestinationPreselected:
        state.isDestinationPreselected ??
        Boolean(idParam || state.selectedWalletId || state.tradingAccountId),
    }
  }, [location.state, idParam])

  const initialSelectedWalletId = useMemo(
    () => locationState.selectedWalletId || (target === 'wallet' ? idParam : undefined),
    [locationState.selectedWalletId, target, idParam]
  )

  const initialTradingAccountId = useMemo(
    () => locationState.tradingAccountId || (target === 'trading-account' ? idParam : undefined),
    [locationState.tradingAccountId, target, idParam]
  )

  const [selectedWalletId, setSelectedWalletId] = useState<string | undefined>(
    initialSelectedWalletId
  )
  const [selectedTradingAccountId, setSelectedTradingAccountId] = useState<string | undefined>(
    initialTradingAccountId
  )

  useEffect(() => {
    const stateUpdate: Partial<DepositStartLocationState> = {}

    if (selectedWalletId && selectedWalletId !== locationState.selectedWalletId) {
      stateUpdate.selectedWalletId = selectedWalletId
    }

    if (selectedTradingAccountId && selectedTradingAccountId !== locationState.tradingAccountId) {
      stateUpdate.tradingAccountId = selectedTradingAccountId
    }

    if (Object.keys(stateUpdate).length > 0) {
      navigate(location.pathname, {
        replace: true,
        state: { ...locationState, ...stateUpdate },
      })
    }
  }, [selectedWalletId, selectedTradingAccountId, locationState, navigate, location.pathname])

  const allowedWalletIdsForDeposit = useMemo(() => {
    const allWallets = contextWallets ?? wallets
    return new Set(
      allWallets
        .filter((w) => isWalletRestrictionsAllowed(w, WalletRestrictions.DEPOSIT))
        .map((w) => w.id)
    )
  }, [contextWallets, wallets])

  const allowedTradingAccounts = useMemo(
    () => tradingAccounts.filter((acc) => allowedWalletIdsForDeposit.has(acc.wallet.id)),
    [allowedWalletIdsForDeposit, tradingAccounts]
  )

  useEffect(() => {
    if (!wallets.length) return

    if (
      initialSelectedWalletId &&
      wallets.some((w) => w.id === initialSelectedWalletId) &&
      initialSelectedWalletId !== selectedWalletId
    ) {
      setSelectedWalletId(initialSelectedWalletId)
      return
    }

    if (selectedWalletId && wallets.some((w) => w.id === selectedWalletId)) return

    const allowedWallets = wallets.filter((w) =>
      isWalletRestrictionsAllowed(w, WalletRestrictions.DEPOSIT)
    )
    const defaultAllowed = allowedWallets.find((w) => w.isDefault)
    setSelectedWalletId((defaultAllowed ?? allowedWallets[0] ?? wallets[0])?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedWalletId, wallets])

  useEffect(() => {
    if (
      initialTradingAccountId &&
      allowedTradingAccounts.some((a) => a.id === initialTradingAccountId)
    ) {
      if (initialTradingAccountId !== selectedTradingAccountId) {
        setSelectedTradingAccountId(initialTradingAccountId)
      }
      return
    }

    if (
      selectedTradingAccountId &&
      allowedTradingAccounts.some((a) => a.id === selectedTradingAccountId)
    ) {
      return
    }

    if (!allowedTradingAccounts.length) {
      return
    }

    setSelectedTradingAccountId(allowedTradingAccounts[0]?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedTradingAccounts, initialTradingAccountId])

  const selectedWallet = useMemo(
    () => wallets.find((w) => w.id === selectedWalletId),
    [wallets, selectedWalletId]
  )

  const selectedTradingAccount = useMemo(
    () => allowedTradingAccounts.find((a) => a.id === selectedTradingAccountId),
    [allowedTradingAccounts, selectedTradingAccountId]
  )

  const isSelectedWalletAllowed = useMemo(() => {
    if (!selectedWallet) return false
    return isWalletRestrictionsAllowed(selectedWallet, WalletRestrictions.DEPOSIT)
  }, [selectedWallet])

  const isTradingAccountAllowed = useCallback(
    (walletId: string) => allowedWalletIdsForDeposit.has(walletId),
    [allowedWalletIdsForDeposit]
  )

  return {
    selectedWalletId,
    setSelectedWalletId,
    selectedTradingAccountId,
    setSelectedTradingAccountId,

    selectedWallet,
    selectedTradingAccount,

    allowedTradingAccounts,

    isSelectedWalletAllowed,

    isTradingAccountAllowed,

    locationState,
  }
}
